import React, {useEffect, useRef, useState} from "react";
import {Link as RouterLink} from "react-router-dom";

function ProjectReveal({children}: {children: React.ReactNode}) {
    const elementRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const element = elementRef.current;

        if (!element || !("IntersectionObserver" in window)) {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                observer.unobserve(entry.target);
            }
        }, {
            threshold: 0.15,
            rootMargin: "0px 0px -8% 0px",
        });

        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={elementRef} className={`project-reveal${isVisible ? " project-reveal--visible" : ""}`}>
            {children}
        </div>
    );
}

type VideoProjectProps = {
    category: string;
    title: string;
    description: React.ReactNode;
    src: string;
    poster?: string;
    videoLabel?: string;
    controlLabel?: string;
    contain?: boolean;
    first?: boolean;
};

function useVideoPlayback() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const togglePlayback = () => {
        if (isPlaying) {
            videoRef.current?.pause();
        } else {
            void videoRef.current?.play();
        }
    };

    return {videoRef, isPlaying, setIsPlaying, togglePlayback};
}

function VideoProject({
    category,
    title,
    description,
    src,
    poster,
    videoLabel = title,
    controlLabel = title,
    contain = false,
    first = false,
}: VideoProjectProps) {
    const {videoRef, isPlaying, setIsPlaying, togglePlayback} = useVideoPlayback();

    return (
        <ProjectReveal>
            <article className={`block ${first ? "border-y" : "border-b"} border-black py-8 md:grid md:grid-cols-[minmax(0,23rem)_1fr_auto] md:items-center md:gap-12 md:py-12`}>
                <div className="aspect-[3/2] overflow-hidden rounded-xl bg-black">
                    <video
                        ref={videoRef}
                        controls
                        playsInline
                        preload="metadata"
                        poster={poster}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                        aria-label={videoLabel}
                        {...(contain ? {width: 406, height: 720} : {})}
                        className={`block h-full w-full ${contain ? "object-contain" : "object-cover"}`}
                    >
                        <source src={src} type="video/mp4"/>
                        Your browser does not support embedded videos.
                    </video>
                </div>
                <div className="mt-6 md:mt-0">
                    <span className="mb-4 block font-['Proxima_Nova_Alt_Light'] text-sm font-bold uppercase tracking-[0.2em] text-gray-500">
                        {category}
                    </span>
                    <h3 className="m-0 text-4xl font-light tracking-wide md:text-6xl">{title}</h3>
                    <p className="mb-0 mt-4 max-w-xl font-['Proxima_Nova_Alt_Light'] text-lg font-normal leading-relaxed text-gray-700">
                        {description}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={togglePlayback}
                    aria-label={`${isPlaying ? "Pause" : "Play"} ${controlLabel} video`}
                    className="mt-8 cursor-pointer border-0 bg-transparent p-0 text-2xl text-black transition-transform duration-200 hover:scale-110 md:mt-1"
                >
                    <span aria-hidden="true">{isPlaying ? "❚❚" : "▶"}</span>
                </button>
            </article>
        </ProjectReveal>
    );
}

type VideoGridItemProps = Omit<VideoProjectProps, "first">;

function VideoGridItem({
    category,
    title,
    description,
    src,
    poster,
    videoLabel = title,
    controlLabel = title,
    contain = false,
}: VideoGridItemProps) {
    const {videoRef, isPlaying, setIsPlaying, togglePlayback} = useVideoPlayback();
    const cardRef = useRef<HTMLElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [areFullscreenControlsVisible, setAreFullscreenControlsVisible] = useState(true);
    const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearControlsTimeout = () => {
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
            controlsTimeoutRef.current = null;
        }
    };

    const showFullscreenControls = () => {
        if (!isFullscreen) {
            return;
        }

        setAreFullscreenControlsVisible(true);
        clearControlsTimeout();
        controlsTimeoutRef.current = setTimeout(() => setAreFullscreenControlsVisible(false), 2000);
    };

    useEffect(() => {
        const video = videoRef.current;
        const handleFullscreenChange = () => setIsFullscreen(document.fullscreenElement === cardRef.current);
        const handleWebkitEnterFullscreen = () => setIsFullscreen(true);
        const handleWebkitExitFullscreen = () => setIsFullscreen(false);

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        video?.addEventListener("webkitbeginfullscreen", handleWebkitEnterFullscreen);
        video?.addEventListener("webkitendfullscreen", handleWebkitExitFullscreen);

        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            video?.removeEventListener("webkitbeginfullscreen", handleWebkitEnterFullscreen);
            video?.removeEventListener("webkitendfullscreen", handleWebkitExitFullscreen);
        };
    }, [videoRef]);

    useEffect(() => {
        clearControlsTimeout();

        if (isFullscreen) {
            setAreFullscreenControlsVisible(true);
            controlsTimeoutRef.current = setTimeout(() => setAreFullscreenControlsVisible(false), 2000);
        } else {
            setAreFullscreenControlsVisible(true);
        }

        return clearControlsTimeout;
    }, [isFullscreen]);

    const toggleFullscreen = () => {
        const video = videoRef.current as (HTMLVideoElement & {
            webkitDisplayingFullscreen?: boolean;
            webkitEnterFullscreen?: () => void;
            webkitExitFullscreen?: () => void;
        }) | null;

        if (document.fullscreenElement) {
            void document.exitFullscreen();
            return;
        }

        if (video?.webkitDisplayingFullscreen) {
            video.webkitExitFullscreen?.();
            return;
        }

        if (cardRef.current?.requestFullscreen) {
            void cardRef.current.requestFullscreen();
            return;
        }

        video?.webkitEnterFullscreen?.();
    };

    return (
        <ProjectReveal>
            <article
                ref={cardRef}
                onMouseMove={showFullscreenControls}
                className={`video-thumbnail group relative aspect-[3/2] overflow-hidden rounded-xl bg-black${isFullscreen && !areFullscreenControlsVisible ? " cursor-none" : ""}`}
            >
                <video
                    ref={videoRef}
                    playsInline
                    preload="metadata"
                    poster={poster}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    aria-label={videoLabel}
                    {...(contain ? {width: 406, height: 720} : {})}
                    className={`pointer-events-none block h-full w-full ${contain ? "object-contain" : "object-cover"}`}
                >
                    <source src={src} type="video/mp4"/>
                    Your browser does not support embedded videos.
                </video>
                <button
                    type="button"
                    onClick={togglePlayback}
                    onFocus={showFullscreenControls}
                    aria-label={`${isPlaying ? "Pause" : "Play"} ${controlLabel} video`}
                    className={`absolute inset-0 border-0 bg-transparent p-0${isFullscreen && !areFullscreenControlsVisible ? " cursor-none" : " cursor-pointer"}`}
                />
                <div className={`pointer-events-none absolute inset-0 flex items-end bg-gradient-to-t from-black/90 via-black/35 to-transparent p-5 text-white transition-opacity duration-300 ${isFullscreen ? (areFullscreenControlsVisible ? "opacity-100" : "opacity-0") : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"}`}>
                    <span className="absolute right-14 top-5 text-2xl drop-shadow-md" aria-hidden="true">
                        {isPlaying ? "❚❚" : "▶"}
                    </span>
                    <div>
                        <span className="mb-2 block font-['Proxima_Nova_Alt_Light'] text-xs font-bold uppercase tracking-[0.2em] text-white/75">
                            {category}
                        </span>
                        <h3 className="m-0 text-2xl font-light tracking-wide">{title}</h3>
                        <p className="mb-0 mt-2 font-['Proxima_Nova_Alt_Light'] text-sm font-normal leading-relaxed text-white/85">
                            {description}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={toggleFullscreen}
                    onFocus={showFullscreenControls}
                    aria-label={`${isFullscreen ? "Exit" : "View"} ${controlLabel} video fullscreen`}
                    className={`absolute right-5 top-5 border-0 bg-transparent p-0 text-2xl text-white drop-shadow-md transition-opacity duration-300 ${isFullscreen ? (areFullscreenControlsVisible ? "cursor-pointer opacity-100" : "cursor-none opacity-0") : "cursor-pointer opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"}`}
                >
                    <span aria-hidden="true">⛶</span>
                </button>
            </article>
        </ProjectReveal>
    );
}

export function Projects() {
    useEffect(() => {
        document.title = "Tom Allton | Projects";

        return () => {
            document.title = "Tom Allton | Home";
        };
    }, []);

    return (
        <div className="projects-page min-h-screen bg-stone-100 text-black">
            <header className="mx-auto flex w-11/12 max-w-6xl items-center justify-between py-8 md:py-12">
                <RouterLink
                    to="/"
                    className="text-lg font-bold uppercase tracking-[0.2em] text-black no-underline"
                >
                    Tom Allton
                </RouterLink>
                <RouterLink
                    to="/"
                    className="font-['Proxima_Nova_Alt_Light'] text-sm font-bold uppercase tracking-[0.16em] text-black no-underline hover:underline"
                >
                    Home
                </RouterLink>
            </header>

            <main className="mx-auto w-11/12 max-w-6xl pb-24 pt-8 text-left md:pb-32 md:pt-14">
                <p className="mb-5 font-['Proxima_Nova_Alt_Light'] text-sm font-bold uppercase tracking-[0.24em] text-gray-500">
                    Selected work
                </p>
                <h1 className="m-0 max-w-4xl text-6xl font-light uppercase leading-none tracking-[0.08em] md:text-8xl">
                    Projects
                </h1>
                <p className="mb-20 mt-8 max-w-2xl font-['Proxima_Nova_Alt_Light'] text-xl font-normal leading-relaxed text-gray-700 md:text-2xl">
                    Digital experiences built at the intersection of software, design, and community.
                </p>

                <section aria-labelledby="main-project-heading">
                    <h2 id="main-project-heading" className="mb-6 text-2xl font-bold uppercase tracking-[0.16em]">
                        Featured Project
                    </h2>
                    <ProjectReveal>
                    <article
                        className="group block border-y border-black py-8 text-black no-underline md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_auto] md:items-center md:gap-12 md:py-12"
                    >
                    <a
                        href="https://pokefind.co/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block overflow-hidden rounded-xl bg-emerald-100"
                    >
                        <img
                            src="/pokefind.png"
                            alt="PokéFind"
                            width="1108"
                            height="660"
                            loading="lazy"
                            className="block aspect-[5/3] h-auto w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        />
                    </a>
                    <div className="mt-6 block md:mt-0">
                        <span className="mb-4 block font-['Proxima_Nova_Alt_Light'] text-sm font-bold uppercase tracking-[0.2em] text-gray-500">
                            Game
                        </span>
                        <h3 className="m-0 text-4xl font-light tracking-wide md:text-6xl">
                            <a href="https://pokefind.co/" target="_blank" rel="noopener noreferrer" className="text-black no-underline">
                                PokéFind
                            </a>
                        </h3>
                        <p className="mb-0 mt-4 block max-w-xl font-['Proxima_Nova_Alt_Light'] text-lg font-normal leading-relaxed text-gray-700">
                            A Pokémon-inspired online multiplayer game enjoyed by more than two million unique players. I serve as creative director and develop community based tools that empower creation.{" "}
                            <a
                                href="https://www.youtube.com/results?search_query=pokefind+"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-700 underline hover:text-black"
                            >
                                10M+ total views on Youtube.
                            </a>
                        </p>
                    </div>
                    <a
                        href="https://pokefind.co/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Visit PokéFind"
                        className="mt-8 inline-block text-3xl text-black no-underline transition-transform duration-200 group-hover:translate-x-2 md:mt-1"
                    >
                        <span aria-hidden="true">&rarr;</span>
                    </a>
                    </article>
                    </ProjectReveal>
                </section>

                <section aria-labelledby="hobby-projects-heading" className="mt-24 md:mt-32">
                    <h2 id="hobby-projects-heading" className="mb-6 text-2xl font-bold uppercase tracking-[0.16em]">
                        Personal Projects
                    </h2>
                    <VideoProject
                        category="Installation"
                        title="Home Lighting System"
                        description="I always wanted to have concert-grade lighting at home, especially when hosting house parties. Built an immersive lighting system that translates concert lighting to be used with consumer smart bulbs. Leverages real-time audio processing that adapts to any song. Currently focused on making it available for anyone with smart lights to use at home."
                        src="/videos/Home Lighting System.mp4"
                        poster="/videos/thumbnails/Home%20Lighting%20System.png"
                        videoLabel="Home Lighting System demonstration"
                        first
                    />
                </section>

                <section aria-labelledby="travel-videos-heading" className="mt-24 md:mt-32">
                    <h2 id="travel-videos-heading" className="mb-0 text-2xl font-bold uppercase tracking-[0.16em]">
                        Videos
                    </h2>
                    <p className="mb-6 mt-3 font-['Proxima_Nova_Alt_Light'] text-lg font-normal leading-relaxed text-gray-700">
                        In my free time I develop short films inspired by my life experiences.
                    </p>
                    <div className="grid gap-6 md:grid-cols-3">
                        <VideoGridItem
                            category="Conceptual"
                            title="Space in the City"
                            description="Capturing moments of stillness within the liminal spaces of my friend's flat in central London, and contrast them to the energy and vibrant chaos of the city streets."
                            src="/videos/Space%20in%20the%20City.mp4"
                            poster="/videos/thumbnails/Space%20in%20the%20City.png"
                        />
                        <VideoGridItem
                            category="Travel"
                            title="Singapore Montage"
                            description="Montage of my week in Singapore, inspired by the city’s resemblance to the “Frutiger Aero” aesthetic and its vision of a technology-driven utopian future."
                            src="/videos/Singapore%20is%20Frutiger%20Aero%20City.mp4"
                            poster="/videos/thumbnails/Singapore%20is%20Frutiger%20Aero%20City.png"
                            videoLabel="Singapore"
                            controlLabel="Singapore"
                        />
                        <VideoGridItem
                            category="Conceptual"
                            title="Space Trip"
                            description="During a time of physical isolation, I found myself yearning for an escape. Space Trip is a short psychedelic film exploring that feeling through a celestial lens."
                            src="/videos/Space%20Trip.mp4"
                            poster="/videos/thumbnails/Space%20Trip.png"
                            contain
                        />
                        <VideoGridItem
                            category="Aerial"
                            title="Drone Cinematic"
                            description="A cinematic aerial study captured through sweeping drone movements with epic orchestral music."
                            src="/videos/Drone%20Cinematic.mp4"
                            poster="/videos/thumbnails/Drone%20Cinematic.png"
                        />
                        <VideoGridItem
                            category="Aerial"
                            title="Drone Follow Test"
                            description="An experiment in how automated drone tracking and follow-shot composition can be used in music videos."
                            src="/videos/Drone%20Follow%20Test.mp4"
                            poster="/videos/thumbnails/Drone%20Follow%20Test.png"
                        />
                    </div>
                </section>
            </main>

            <footer className="mx-auto w-11/12 max-w-6xl pb-8 text-left font-['Proxima_Nova_Alt_Light'] text-sm font-normal text-gray-600">
                Copyright © Tom Allton {new Date().getFullYear()}. All Rights Reserved.
            </footer>
        </div>
    );
}
